import { useState } from 'react'
import { formatDateTime } from '@/lib/date'
import type { Snippet } from '@/types'
import { SnippetForm } from './SnippetForm'

interface SnippetCardProps {
  snippet: Snippet
  onUpdate: (id: string, content: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

export const SnippetCard = ({
  snippet,
  onUpdate,
  onDelete,
}: SnippetCardProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleUpdate = async (content: string) => {
    await onUpdate(snippet.id, content)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (window.confirm('Delete this snippet?')) {
      setIsDeleting(true)
      try {
        await onDelete(snippet.id)
      } catch {
        setIsDeleting(false)
      }
    }
  }

  if (isEditing) {
    return (
      <div className="card">
        <SnippetForm
          initialValue={snippet.content}
          onSubmit={handleUpdate}
          onCancel={() => setIsEditing(false)}
          submitLabel="Save"
        />
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex justify-between items-start mb-2">
        <p className="text-sm text-gray-500">
          {formatDateTime(snippet.createdAt)}
        </p>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
      <p className="text-gray-900 whitespace-pre-wrap">{snippet.content}</p>
    </div>
  )
}
